import type { JSX } from 'react';
import type {
  ActivityResource,
  DealSummaryResource,
  LogActivityBody,
  UpdateActivityBody,
} from '@majuscule/contracts';
import { useMutation } from '@tanstack/react-query';
import {
  ArrowRight,
  Bell,
  CalendarCheck,
  CalendarX,
  Check,
  ClipboardList,
  FileText,
  Mail,
  Phone,
  Pin,
  PinOff,
  Plus,
  ReceiptEuro,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { assertNever } from '@majuscule/domain';
import {
  activityTitle,
  crmSend,
  DISCOVERY_FIELDS,
  errorMessage,
  formatInstantDate,
  LOGGED_KIND_LABEL,
  LOGGED_KIND_PLACEHOLDER,
  LOGGED_KINDS,
  type LoggedKind,
} from '../../lib/crm';
import { useCrmRefresh } from './shared';
import { Button } from '@/components/ui/button';
import { CheckRow, FieldError, NativeCheckbox } from '@/components/ui/field';
import { NativeSelect } from '@/components/ui/native-select';
import { TableTabs } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

function activityIcon(activity: ActivityResource): LucideIcon {
  const { content } = activity;
  switch (content.kind) {
    case 'note':
      return FileText;
    case 'call':
      return Phone;
    case 'meeting':
      return Users;
    case 'email':
      return Mail;
    case 'discovery':
      return ClipboardList;
    case 'system': {
      const { event } = content;
      switch (event.type) {
        case 'company_created':
        case 'deal_created':
          return Plus;
        case 'stage_changed':
          return ArrowRight;
        case 'deal_won':
        case 'accounts_created':
        case 'reminder_done':
          return Check;
        case 'deal_lost':
          return X;
        case 'reminder_created':
          return Bell;
        case 'proposal_sent':
        case 'document_added':
          return FileText;
        case 'proposal_accepted':
        case 'installment_paid':
          return Check;
        case 'proposal_declined':
          return X;
        case 'installment_invoiced':
          return ReceiptEuro;
        case 'appointment_booked':
        case 'appointment_rescheduled':
          return CalendarCheck;
        case 'appointment_cancelled':
          return CalendarX;
        default:
          return assertNever(event);
      }
    }
    default:
      return assertNever(content);
  }
}

function activityBody(activity: ActivityResource): string | null {
  const { content } = activity;
  switch (content.kind) {
    case 'note':
    case 'call':
    case 'meeting':
    case 'email':
      return content.body;
    case 'discovery': {
      const first = DISCOVERY_FIELDS.find((field) => content.report[field.key] !== '');
      return first === undefined ? null : `${first.label} : ${content.report[first.key]}`;
    }
    case 'system':
      return null;
    default:
      return assertNever(content);
  }
}

/** `feed` : journal of a company or a deal, newest first. */
export function ActivityFeed({
  activities,
  showDeal,
  label,
}: {
  activities: readonly ActivityResource[];
  /** Company page: the linked deal goes in the meta line. */
  showDeal: boolean;
  label: string;
}): JSX.Element {
  const refresh = useCrmRefresh();
  const pin = useMutation({
    mutationFn: ({ id, pinned }: { id: string; pinned: boolean }) => {
      const body: UpdateActivityBody = { pinned };
      return crmSend<ActivityResource>('PATCH', `/activities/${id}`, body);
    },
    onSuccess: () => {
      refresh();
    },
  });

  if (activities.length === 0) {
    return (
      <p className="m-0 px-4 py-8 text-center text-[13px] text-muted-foreground">
        Aucune activité pour l’instant.
      </p>
    );
  }
  return (
    <>
      {pin.isError ? (
        <FieldError className="px-4 pt-2">
          {errorMessage(pin.error, 'Échec de l’épinglage.')}
        </FieldError>
      ) : null}
      <ul aria-label={label} className="m-0 grid list-none px-4 pb-4 pt-2">
        {activities.map((activity) => {
          const Icon = activityIcon(activity);
          const system = activity.content.kind === 'system';
          const body = activityBody(activity);
          const meta = [
            showDeal ? activity.dealTitle : null,
            system ? null : activity.author,
            activity.pinned ? 'Épinglé' : null,
          ]
            .filter((part): part is string => part !== null && part !== '')
            .join(', ');
          return (
            <li
              key={activity.id}
              className="grid grid-cols-[28px_minmax(0,1fr)] gap-3 border-b border-border py-3 last:border-b-0"
            >
              <span
                aria-hidden="true"
                className={cn(
                  'grid size-7 place-items-center rounded-md border border-border [&_svg]:size-3.5',
                  system ? 'text-muted-foreground' : 'bg-muted text-foreground',
                )}
              >
                <Icon />
              </span>
              <div className="grid min-w-0 gap-1">
                <div className="flex items-baseline justify-between gap-3">
                  <b
                    className={cn(
                      'text-[13px] leading-[18px]',
                      system ? 'font-normal text-muted-foreground' : 'font-medium',
                    )}
                  >
                    {activityTitle(activity)}
                  </b>
                  <span className="flex shrink-0 items-center gap-1">
                    <span className="whitespace-nowrap font-mono text-xs text-muted-foreground">
                      {formatInstantDate(activity.occurredAt)}
                    </span>
                    {system ? null : (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={activity.pinned ? 'Désépingler' : 'Épingler'}
                        title={activity.pinned ? 'Désépingler' : 'Épingler'}
                        disabled={pin.isPending}
                        onClick={() => {
                          pin.mutate({ id: activity.id, pinned: !activity.pinned });
                        }}
                      >
                        {activity.pinned ? (
                          <PinOff aria-hidden="true" />
                        ) : (
                          <Pin aria-hidden="true" />
                        )}
                      </Button>
                    )}
                  </span>
                </div>
                {body !== null ? (
                  <p className="m-0 whitespace-pre-line text-[13px] leading-5">{body}</p>
                ) : null}
                {meta !== '' ? (
                  <div className="text-xs leading-4 text-muted-foreground">{meta}</div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
}

/** `composer` : quick entry of a note, call, meeting or email. */
export function ActivityComposer({
  companyId,
  dealId,
  deals,
}: {
  companyId: string;
  /** Deal page: the entry is linked to this deal (pin option). */
  dealId?: string;
  /** Company page: deals the entry may be linked to. */
  deals?: readonly DealSummaryResource[];
}): JSX.Element {
  const refresh = useCrmRefresh();
  const [kind, setKind] = useState<LoggedKind>('note');
  const [body, setBody] = useState('');
  const [pinned, setPinned] = useState(false);
  const openDeals = (deals ?? []).filter((deal) => deal.stage !== 'won' && deal.stage !== 'lost');
  const [linkedDeal, setLinkedDeal] = useState<string>(
    openDeals.length === 1 ? (openDeals[0]?.id ?? '') : '',
  );

  const log = useMutation({
    mutationFn: () => {
      const payload: LogActivityBody = {
        kind,
        companyId,
        dealId: dealId ?? (linkedDeal === '' ? null : linkedDeal),
        body: body.trim(),
        pinned,
      };
      return crmSend<ActivityResource>('POST', '/activities', payload);
    },
    onSuccess: () => {
      setBody('');
      setPinned(false);
      refresh();
    },
  });

  return (
    <form
      className="grid gap-2 border-b border-border p-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (body.trim() !== '') {
          log.mutate();
        }
      }}
    >
      <TableTabs<LoggedKind>
        aria-label="Type d’activité"
        className="justify-self-start"
        value={kind}
        onValueChange={setKind}
        items={LOGGED_KINDS.map((value) => ({ value, label: LOGGED_KIND_LABEL[value] }))}
      />
      <label className="sr-only" htmlFor={`composer-${dealId ?? companyId}`}>
        Contenu de l’activité
      </label>
      <Textarea
        id={`composer-${dealId ?? companyId}`}
        rows={3}
        maxLength={10_000}
        placeholder={LOGGED_KIND_PLACEHOLDER[kind]}
        value={body}
        onChange={(event) => {
          setBody(event.target.value);
        }}
      />
      <div className="flex flex-wrap items-center justify-between gap-2">
        {dealId !== undefined ? (
          <CheckRow
            label="Épingler en haut de l’affaire"
            checked={pinned}
            onChange={(event) => {
              setPinned(event.target.checked);
            }}
          />
        ) : (
          <NativeSelect
            controlSize="sm"
            wrapperClassName="min-w-[220px]"
            aria-label="Affaire liée"
            value={linkedDeal}
            onChange={(event) => {
              setLinkedDeal(event.target.value);
            }}
          >
            <option value="">Aucune affaire</option>
            {(deals ?? []).map((deal) => (
              <option key={deal.id} value={deal.id}>
                {deal.title}
              </option>
            ))}
          </NativeSelect>
        )}
        <span className="flex items-center gap-2">
          {dealId === undefined ? (
            <label className="flex items-center gap-1.5 text-[13px]">
              <NativeCheckbox
                checked={pinned}
                onChange={(event) => {
                  setPinned(event.target.checked);
                }}
              />
              Épingler
            </label>
          ) : null}
          <Button type="submit" size="sm" disabled={log.isPending || body.trim() === ''}>
            Ajouter au journal
          </Button>
        </span>
      </div>
      {log.isError ? (
        <FieldError>{errorMessage(log.error, 'Échec de l’ajout au journal.')}</FieldError>
      ) : null}
    </form>
  );
}
